/**
 * MergeManager — fusion de versions (branches) d'un plan, côté moteur.
 *
 * Travaille sur des bases ANNEXES ouvertes depuis les DWG des révisions (base,
 * cible, fusionnée), jamais sur la base affichée : le plan ouvert n'est pas
 * modifié, le résultat est écrit en DWG et envoyé comme révision de fusion.
 *
 * Une fusion est une SESSION : `openSession` lit les trois versions et garde
 * ouvertes la cible et la fusionnée (aperçu des conflits, application) ;
 * `closeSession` les libère (mémoire WASM), après avoir retiré l'aperçu dont les
 * fantômes y sont rattachés. `apply` termine la session : la cible est modifiée.
 * La lecture rend la main au navigateur : la fermer entre-temps l'abandonne.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type {
  DrawingSnapshot,
  MergeConflict,
  MergePlan,
} from "@/utils/branch-merge/types";
import { deleteObj } from "../core/delete";
import {
  describeEntity,
  snapshotDatabase,
  SnapshotCancelled,
  type SnapshotOptions,
} from "../merge/snapshot";
import {
  applyMergePlan,
  writeDatabaseToBuffer,
  type ApplyMergeReport,
} from "../merge/apply";
import { getPhotomarkerLastNumber } from "../photomarker/xrecord";
import { MergePreview, type PreviewVersion } from "../merge/preview";

type SideDatabase = DrawingModule.OdDbDatabase;

/** DWG des trois versions : origine commune, cible et fusionnée. */
export interface MergeDwgs {
  base: ArrayBuffer;
  target: ArrayBuffer;
  merged: ArrayBuffer;
}

export interface MergeSnapshots {
  base: DrawingSnapshot;
  target: DrawingSnapshot;
  merged: DrawingSnapshot;
}

/** Couleur des fantômes de chaque version. */
export type MergeSideColors = Record<
  "target" | "merged",
  PreviewVersion["rgb"]
>;

export class MergeManager {
  private preview: MergePreview;
  private targetDb: SideDatabase | null = null;
  private mergedDb: SideDatabase | null = null;
  /** Change à chaque ouverture ou fermeture : une lecture en cours devient caduque. */
  private sessionVersion = 0;

  constructor(private viewer: DRDrawingViewer) {
    this.preview = new MergePreview(viewer);
  }

  get hasSession(): boolean {
    return !!this.targetDb && !!this.mergedDb;
  }

  /**
   * Ouvre une session (en remplace une éventuelle précédente) : l'origine n'est
   * ouverte que le temps de son instantané, la cible et la fusionnée restent
   * ouvertes jusqu'à `closeSession` ou `apply`. Rejette avec `SnapshotCancelled`
   * si la session est fermée pendant la lecture. `onProgress` : version en cours
   * de lecture (1 à 3) et nombre d'entités lues.
   */
  async openSession(
    dwgs: MergeDwgs,
    onProgress?: (version: number, count: number) => void,
  ): Promise<MergeSnapshots> {
    this.closeSession();
    const session = this.sessionVersion;
    const options = (version: number): SnapshotOptions => ({
      isCancelled: () => session !== this.sessionVersion,
      onProgress: (count) => onProgress?.(version, count),
    });
    // Gardées à part jusqu'au bout : `closeSession` ne doit pas libérer une base
    // en cours de parcours, c'est la lecture abandonnée qui s'en charge.
    let targetDb: SideDatabase | null = null;
    let mergedDb: SideDatabase | null = null;
    try {
      const base = await this.snapshotDwg(dwgs.base, options(1));
      targetDb = this.open("__merge_target.dwg", dwgs.target);
      const target = await this.snapshot(targetDb, options(2));
      mergedDb = this.open("__merge_merged.dwg", dwgs.merged);
      const merged = await this.snapshot(mergedDb, options(3));
      if (session !== this.sessionVersion) throw new SnapshotCancelled();
      this.targetDb = targetDb;
      this.mergedDb = mergedDb;
      return { base, target, merged };
    } catch (e) {
      if (targetDb) deleteObj(targetDb);
      if (mergedDb) deleteObj(mergedDb);
      throw e;
    }
  }

  /** Ajoute leurs données d'affichage aux versions des entités en conflit. */
  describeConflicts(conflicts: MergeConflict[]): void {
    const { targetDb, mergedDb } = this.requireSession();
    const module = this.viewer.module;
    for (const { target, merged } of conflicts) {
      if (target)
        Object.assign(target, describeEntity(module, targetDb, target.handle));
      if (merged)
        Object.assign(merged, describeEntity(module, mergedDb, merged.handle));
    }
  }

  /** Instantané d'un DWG, ouvert le temps de le lire. */
  async snapshotDwg(
    buffer: ArrayBuffer,
    options?: SnapshotOptions,
  ): Promise<DrawingSnapshot> {
    const db = this.open("__merge_snapshot.dwg", buffer);
    try {
      return await this.snapshot(db, options);
    } finally {
      deleteObj(db);
    }
  }

  /** Ouvre un DWG et le réécrit tel quel (banc d'essai : stabilité des empreintes). */
  resaveDwg(buffer: ArrayBuffer): Uint8Array {
    const db = this.open("__merge_resave.dwg", buffer);
    try {
      return writeDatabaseToBuffer(this.viewer.module, db);
    } finally {
      deleteObj(db);
    }
  }

  /** Ouvre un DWG, le retouche et l'écrit (banc d'essai : conflits fabriqués). */
  editDwg(
    buffer: ArrayBuffer,
    edit: (module: DrawingModuleType, db: SideDatabase) => void,
  ): Uint8Array {
    const module = this.viewer.module;
    const db = this.open("__merge_edit.dwg", buffer);
    try {
      edit(module, db);
      return writeDatabaseToBuffer(module, db);
    } finally {
      deleteObj(db);
    }
  }

  /** Le moteur chargé sait écrire un DWG (cf. `checkWriteCapability`). */
  canWrite(): Promise<boolean> {
    return this.viewer.checkWriteCapability();
  }

  /** Retire l'aperçu puis libère les bases de la session (sans effet sans session). */
  closeSession(): void {
    this.sessionVersion++;
    // Les fantômes sont clonés des bases annexes : ils partent AVANT elles.
    this.preview.clear();
    if (this.targetDb) deleteObj(this.targetDb);
    if (this.mergedDb) deleteObj(this.mergedDb);
    this.targetDb = null;
    this.mergedDb = null;
  }

  /**
   * Montre en fantômes, sur le plan affiché, les versions présentes d'une entité
   * en conflit : les deux si elle est modifiée des deux côtés, la seule restante
   * face à une suppression.
   */
  showConflict(conflict: MergeConflict, colors: MergeSideColors): void {
    const versions: PreviewVersion[] = [];
    if (conflict.target && this.targetDb) {
      versions.push({
        db: this.targetDb,
        handle: conflict.target.handle,
        rgb: colors.target,
      });
    }
    if (conflict.merged && this.mergedDb) {
      versions.push({
        db: this.mergedDb,
        handle: conflict.merged.handle,
        rgb: colors.merged,
      });
    }
    this.preview.show(versions);
  }

  clearPreview(): void {
    this.preview.clear();
  }

  /** Applique le plan à la cible, renvoie le DWG résultat et ferme la session. */
  apply(plan: MergePlan): { report: ApplyMergeReport; dwg: Uint8Array } {
    const { targetDb, mergedDb } = this.requireSession();
    const module = this.viewer.module;
    try {
      const report = applyMergePlan(
        module,
        this.viewer.services.layoutManager(),
        targetDb,
        mergedDb,
        plan,
        // Le prochain marqueur repart au-dessus des deux versions.
        Math.max(
          getPhotomarkerLastNumber(module, targetDb, 0),
          getPhotomarkerLastNumber(module, mergedDb, 0),
        ),
      );
      return { report, dwg: writeDatabaseToBuffer(module, targetDb) };
    } finally {
      this.closeSession();
    }
  }

  private requireSession(): { targetDb: SideDatabase; mergedDb: SideDatabase } {
    if (!this.targetDb || !this.mergedDb) {
      throw new Error("MergeManager : aucune session de fusion ouverte");
    }
    return { targetDb: this.targetDb, mergedDb: this.mergedDb };
  }

  private open(name: string, buffer: ArrayBuffer): SideDatabase {
    return this.viewer.openDatabaseFromBuffer(name, buffer);
  }

  private snapshot(
    db: SideDatabase,
    options?: SnapshotOptions,
  ): Promise<DrawingSnapshot> {
    return snapshotDatabase(this.viewer.module, db, options);
  }
}
